/**
 * Activity state-glyph X registry — the painter→interaction bridge for the
 * canvas state-picker anchor (WI #5560).
 *
 * The activity banner's state glyph is drawn at the card's far edge, whose X
 * depends on the painter-measured card width and flip (BannerPainter
 * `left + width - 20`). Interaction code (the state-picker popover) therefore
 * cannot recompute it. The painter records each shown banner's glyph
 * CONTENT-space centre X here every frame; the popover reads it to anchor the
 * state dropdown beside the glyph (open left, flip right when there is no room).
 *
 * This supersedes the older `bannerFlipState` side bridge (WI #5551): the glyph
 * X carries the banner side implicitly (far right when normal, left of the
 * hexagon when flipped), so the popover no longer needs a separate flip flag.
 *
 * Mirrors the `activityLayout` engine's reactive-motion pattern: `get` touches
 * a `version` ref so Vue computeds built on it (the popover's `selectorX`)
 * re-evaluate when a glyph moves (e.g. a pan/zoom shifts a banner while the
 * picker is open). `set` only bumps the version when the values actually change,
 * so the per-frame painter write is a no-op for reactivity in the common
 * (unchanged) case.
 */
class ActivityGlyphX {
  /** Reactive change counter; `get` touches it, `set` bumps it. */
  private readonly version = ref(0);
  private glyphX = new Map<string, number>();

  /** Painter writes the glyph content-space centre X of the current frame. */
  set(next: Map<string, number>): void {
    if (sameMap(next, this.glyphX)) {
      return;
    }
    this.glyphX = next;
    this.version.value++;
  }

  /** Interaction reads an activity's glyph content X this frame (or undefined). */
  get(id: string): number | undefined {
    // Reactive touch (see `version`): lets Vue computeds re-evaluate when the
    // glyph set changes; a no-op for non-reactive callers.
    void this.version.value;

    return this.glyphX.get(id);
  }

  /** Drop all glyph state (renderer teardown / map switch). */
  reset(): void {
    if (this.glyphX.size) {
      this.glyphX = new Map();
      this.version.value++;
    }
  }
}

function sameMap(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const [id, x] of a) {
    if (b.get(id) !== x) {
      return false;
    }
  }

  return true;
}

/** The singleton glyph-X registry — one map view exists at a time. */
export const activityGlyphX = new ActivityGlyphX();
